// ============================================
// Wardrobe Overlay
// Replaces WardrobeOverlayController.cs
// ============================================

import { events, Events } from '../engine/events.js';
import { UITags, EquipSlots, EquipSlotNames, MULTI_SLOT_MAP } from '../engine/constants.js';
import { variableManager } from '../engine/variables.js';
import { SET_BONUSES, inventoryManager } from '../systems/inventory.js';
import { L } from '../systems/localization.js';
import { paperDoll } from './paper-doll.js';

// A locked wardrobe button looks disabled but stays TAPPABLE so its reason can
// surface as a toast on touch. A native `disabled` button fires no click, and
// `.wardrobe-btn-disabled` sets `pointer-events:none`, so the only explanation
// was the `title=` tooltip, which is hover-only and invisible on iOS. The
// player just saw a dead button and reported "wardrobe won't let me change my
// outfit". foundby:VPS iPhone player 0.2.9.0.6.
function _markLockedBtn(btn, label, reason) {
    btn.textContent = label;
    if (reason) btn.title = reason;
    btn.classList.add('wardrobe-btn-disabled');
    btn.style.pointerEvents = 'auto';
    btn.style.cursor = 'help';
    if (reason) {
        btn.addEventListener('click', () => events.emit(Events.NOTIFICATION_REQUEST, reason));
    }
}

// Max outfit preset slots
const MAX_PRESETS = 5;

// Undress restriction: can't remove Upper/Lower/Feet outside apartment (matches Unity WardrobeOverlayController)
const RESTRICTED_SLOTS = [EquipSlots.UPPER, EquipSlots.LOWER, EquipSlots.FEET];

// Plug first-equip: tag -> Ink variable that tracks if first-equip scene has played
const PLUG_FIRST_EQUIP_MAP = {
    'plug':            'plug_first_equip_small',
    'plug_medium':     'plug_first_equip_medium',
    'plug_large':      'plug_first_equip_large',
    'vibrating_plug':  'plug_first_equip_small',
    'remote_vib':      'vib_first_equip',
};

// Ink variable -> forced event knot name
const PLUG_FIRST_EQUIP_KNOTS = {
    'plug_first_equip_small':  'Plug_FirstEquip_Small',
    'plug_first_equip_medium': 'Plug_FirstEquip_Medium',
    'plug_first_equip_large':  'Plug_FirstEquip_Large',
    'vib_first_equip':         'Vib_FirstEquip',
};

class WardrobeUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {boolean} */
        this._isOpen = false;

        /** @type {number} Active slot tab index */
        this._activeSlot = 0;

        /** @type {Map<number, object|null>} Slot -> equipped item */
        this._equipped = new Map();

        /** @type {Array<object>} All owned items */
        this._ownedItems = [];

        /** @type {Array<object|null>} Outfit presets (3 slots) */
        this._presets = [null, null, null, null, null];

        /** @type {string} Current location for undress restriction */
        this._currentLocation = 'Home';

        /** @type {Array<object>} Active set bonuses */
        this._activeSets = [];

        /** @type {boolean} Flash animation pending */
        this._flashPending = false;

        this._scope = events.createScope('wardrobe');
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this.close());
        this._loadPresets();
        this._bindEvents();
    }

    /** Detach all event listeners (tests + future re-init paths). */
    destroy() {
        this._scope.destroy();
    }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        this._root = document.getElementById('overlay-wardrobe');
        if (!this._root) {
            this._root = this._createContainer();
        }
    }

    open() {
        if (!this._root) this.init();
        this._isOpen = true;
        this._root.classList.remove('hidden');

        // Always sync inventory from source of truth before rendering
        this._ownedItems = inventoryManager.getOwnedItemObjects();
        this._equipped = inventoryManager.getEquippedMap();
        this._activeSets = inventoryManager.getActiveSets();

        // Default to "All" tab
        this._activeSlot = -1;

        this._buildUI();
        this._renderSlotItems();

        // Keyboard nav: arrow keys switch tabs, Escape closes
        document.removeEventListener('keydown', this._keyHandler);
        this._keyHandler = (e) => {
            if (!this._isOpen) return;
            const tabs = this._root?.querySelectorAll('.wardrobe-tab');
            if (!tabs || !tabs.length) return;
            const tabSlots = [...tabs].map(t => parseInt(t.dataset.slot));
            const curIdx = tabSlots.indexOf(this._activeSlot);
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                const next = (curIdx + 1) % tabSlots.length;
                this._activeSlot = tabSlots[next];
                this._updateTabs(); this._renderSlotItems();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = (curIdx - 1 + tabSlots.length) % tabSlots.length;
                this._activeSlot = tabSlots[prev];
                this._updateTabs(); this._renderSlotItems();
            } else if (e.key === 'Escape') {
                this.close();
            }
        };
        document.addEventListener('keydown', this._keyHandler);

        if (this._flashPending) {
            this._flashPending = false;
            this._root.classList.add('wardrobe-flash');
            setTimeout(() => this._root.classList.remove('wardrobe-flash'), 600);
        }

        events.emit(Events.OVERLAY_OPEN, 'overlay-wardrobe');
    }

    close() {
        this._isOpen = false;
        document.removeEventListener('keydown', this._keyHandler);
        // Clean up any orphaned tooltips appended to document.body
        document.querySelectorAll('.wardrobe-preview-tooltip').forEach(el => el.remove());
        // Clean up any lingering set-equip toasts
        document.querySelectorAll('.set-equip-toast').forEach(el => el.remove());
        // Clean up any open preset menu / confirm / name-input modals appended to body
        document.querySelectorAll('.wardrobe-preset-menu, .wardrobe-discard-confirm, .wardrobe-name-modal').forEach(el => el.remove());
        // Let layout.closeOverlay handle hiding + backdrop
        events.emit(Events.OVERLAY_CLOSE, 'overlay-wardrobe');
    }

    /**
     * Update the full inventory of owned items.
     * @param {Array<object>} items - [{tag, name, slot, buffs, description, icon, setId}]
     */
    updateInventory(items) {
        this._ownedItems = items;
        if (this._isOpen) this._renderSlotItems();
    }

    /**
     * Update currently equipped items.
     * @param {Map<number, object|null>|object} equipped - slot -> item
     */
    updateEquipped(equipped) {
        if (equipped instanceof Map) {
            this._equipped = equipped;
        } else {
            this._equipped = new Map();
            for (const [slot, item] of Object.entries(equipped)) {
                this._equipped.set(parseInt(slot), item);
            }
        }
        if (this._isOpen) this._renderSlotItems();
    }

    /**
     * Update active set bonuses.
     * @param {Array<object>} sets - [{name, bonuses}]
     */
    updateSetBonuses(sets) {
        this._activeSets = sets || [];
        if (this._isOpen) this._renderSlotItems();
    }

    /** Set current location for undress restrictions. */
    setLocation(location) {
        this._currentLocation = location;
    }

    /** Trigger flash animation on next open. */
    triggerFlash() {
        this._flashPending = true;
    }

    isOpen() {
        return this._isOpen;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UITags.WARDROBE) {
                this.open();
            } else if (this._isOpen) {
                this.close();
            }
        });

        this._scope.on(Events.ITEM_ADDED, (item) => {
            if (item) {
                this._flashPending = true;
            }
        });

        this._scope.on(Events.ITEM_EQUIPPED, (item, slot) => {
            if (slot !== undefined) {
                this._equipped.set(slot, item);
                if (this._isOpen) this._renderSlotItems();
            }
        });

        this._scope.on(Events.ITEM_UNEQUIPPED, (item, slot) => {
            if (slot !== undefined) {
                this._equipped.set(slot, null);
                if (this._isOpen) this._renderSlotItems();
            }
        });

        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            if (name === 'CurrentLocation') {
                this._currentLocation = value || 'Home';
            }
        });
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _buildUI() {
        // Build inside the .overlay-box container, not the overlay root
        // This preserves the overlay positioning + background styling from CSS
        let box = this._root.querySelector('.overlay-box');
        if (!box) {
            // Fallback: create the box structure if missing
            box = document.createElement('div');
            box.className = 'overlay-box wardrobe-box';
            this._root.innerHTML = '';
            this._root.appendChild(box);
        }
        box.innerHTML = '';

        const frame = document.createElement('div');
        frame.className = 'wardrobe-frame';

        // Floating close button (top-right)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'overlay-close-float';
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', () => this.close());
        frame.appendChild(closeBtn);

        // Banner
        const banner = document.createElement('div');
        banner.className = 'wardrobe-banner';
        banner.innerHTML = `
            <span class="wardrobe-banner-icon">\uD83D\uDC57</span>
            <div class="wardrobe-banner-text">
                <div class="wardrobe-banner-title">${L.get('wardrobe.title')}</div>
                <div class="wardrobe-banner-sub">${this._ownedItems.length} ${L.get('wardrobe.items_owned')}</div>
            </div>
        `;
        frame.appendChild(banner);

        // Slot tabs
        const tabs = document.createElement('div');
        tabs.className = 'wardrobe-tabs';
        tabs.id = 'wardrobe-tabs';

        // "All" tab first
        const allTab = document.createElement('button');
        allTab.className = 'wardrobe-tab' + (this._activeSlot === -1 ? ' wardrobe-tab-active' : '');
        allTab.textContent = L.get('wardrobe.all');
        allTab.dataset.slot = '-1';
        allTab.addEventListener('click', () => {
            this._activeSlot = -1;
            this._updateTabs();
            this._renderSlotItems();
        });
        tabs.appendChild(allTab);

        // Collect sub-slots to hide (Accessory2-4, Piercing2-3)
        const _hiddenSlots = new Set();
        for (const slots of Object.values(MULTI_SLOT_MAP)) {
            for (let j = 1; j < slots.length; j++) _hiddenSlots.add(slots[j]);
        }

        const _multiLabels = { 'Accessory': 'wardrobe.accessories', 'Piercing': 'wardrobe.piercings', 'Cosmetic': 'wardrobe.cosmetics' };

        for (let i = 0; i < EquipSlotNames.length; i++) {
            const name = EquipSlotNames[i];
            // Skip sub-slots — merged into their base tab
            if (_hiddenSlots.has(name)) continue;

            const tab = document.createElement('button');
            const multiSlots = MULTI_SLOT_MAP[name];
            tab.className = 'wardrobe-tab' + (i === this._activeSlot ? ' wardrobe-tab-active' : '');
            tab.textContent = multiSlots ? (L.get(_multiLabels[name]) || name + 's') : name;
            tab.dataset.slot = i;

            // Show equipped indicator
            if (multiSlots) {
                const hasAny = multiSlots.some(s => this._equipped.get(s));
                if (hasAny) tab.classList.add('wardrobe-tab-equipped');
            } else {
                const equipped = this._equipped.get(name) || this._equipped.get(i);
                if (equipped) tab.classList.add('wardrobe-tab-equipped');
            }

            tab.addEventListener('click', () => {
                this._activeSlot = i;
                this._updateTabs();
                this._renderSlotItems();
            });
            tabs.appendChild(tab);
        }

        frame.appendChild(tabs);

        // Search input
        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.placeholder = L.get('wardrobe.search');
        searchInput.className = 'wardrobe-search';
        searchInput.addEventListener('input', () => this._filterItems(searchInput.value));
        frame.appendChild(searchInput);

        // Split row: paper-doll preview left, item grid right.
        // The wardrobe DOM is fully JS-built, so the pane must be
        // (re)created here - a static index.html div would be wiped by
        // box.innerHTML = '' (PAPER_DOLL.md Phase 3).
        const split = document.createElement('div');
        split.className = 'wardrobe-split';

        const dollPane = document.createElement('div');
        dollPane.className = 'wardrobe-doll-pane';
        dollPane.id = 'wardrobe-doll';
        split.appendChild(dollPane);

        const itemsContainer = document.createElement('div');
        itemsContainer.className = 'wardrobe-items';
        itemsContainer.id = 'wardrobe-items';
        split.appendChild(itemsContainer);

        frame.appendChild(split);
        paperDoll.mount(dollPane);

        // Set bonuses section
        const setsContainer = document.createElement('div');
        setsContainer.className = 'wardrobe-sets';
        setsContainer.id = 'wardrobe-sets';
        frame.appendChild(setsContainer);

        // Outfit presets
        const presetsBar = document.createElement('div');
        presetsBar.className = 'wardrobe-presets';
        presetsBar.innerHTML = `<span class="wardrobe-presets-label">${L.get('wardrobe.outfits')}:</span>`;

        for (let i = 0; i < MAX_PRESETS; i++) {
            const preset = this._presets[i];
            if (!preset) continue;
            const btn = document.createElement('button');
            btn.className = 'wardrobe-preset-btn wardrobe-preset-saved';
            btn.textContent = preset.name || `#${i + 1}`;
            btn.title = L.get('wardrobe.click_to_load') || 'Click to load';

            // Long-press (500ms) → menu, short tap → load
            let pressTimer = null;
            let didLongPress = false;
            const startPress = (e) => {
                didLongPress = false;
                pressTimer = setTimeout(() => {
                    didLongPress = true;
                    this._showPresetMenu(i, btn);
                }, 500);
            };
            const endPress = (e) => {
                clearTimeout(pressTimer);
                if (!didLongPress) {
                    this._loadPreset(i);
                }
            };
            const cancelPress = () => { clearTimeout(pressTimer); };
            btn.addEventListener('mousedown', startPress);
            btn.addEventListener('mouseup', endPress);
            btn.addEventListener('mouseleave', cancelPress);
            btn.addEventListener('touchstart', startPress, { passive: true });
            btn.addEventListener('touchend', (e) => { e.preventDefault(); endPress(e); });
            btn.addEventListener('touchcancel', cancelPress);
            btn.addEventListener('contextmenu', (e) => e.preventDefault());

            // "⋮" menu button next to preset
            const menuBtn = document.createElement('button');
            menuBtn.className = 'wardrobe-preset-btn wardrobe-preset-menu-btn';
            menuBtn.textContent = '⋮';
            menuBtn.title = L.get('wardrobe.manage') || 'Manage';
            menuBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._showPresetMenu(i, btn);
            });

            presetsBar.appendChild(btn);
            presetsBar.appendChild(menuBtn);
        }

        // "+" button to save new outfit (only if we haven't hit the limit)
        const savedCount = this._presets.filter(p => p !== null).length;
        if (savedCount < MAX_PRESETS) {
            const addBtn = document.createElement('button');
            addBtn.className = 'wardrobe-preset-btn wardrobe-preset-add';
            addBtn.textContent = '+';
            addBtn.title = L.get('wardrobe.save_outfit_tip') || 'Save current outfit';
            addBtn.addEventListener('click', () => {
                this._showNameInput({
                    title: L.get('wardrobe.name_outfit') || 'Name your outfit',
                    placeholder: L.get('wardrobe.outfit_name_ph') || 'Outfit name',
                    confirmLabel: L.get('wardrobe.save') || 'Save',
                    onConfirm: (name) => {
                        const trimmed = (name || '').trim() || `Outfit ${savedCount + 1}`;
                        // Find first empty slot
                        const emptyIdx = this._presets.findIndex(p => p === null);
                        if (emptyIdx === -1) return;
                        this._savePreset(emptyIdx, trimmed);
                    },
                });
            });
            presetsBar.appendChild(addBtn);
        }

        frame.appendChild(presetsBar);
        box.appendChild(frame);
    }

    _updateTabs() {
        const tabs = this._root.querySelectorAll('.wardrobe-tab');
        tabs.forEach(tab => {
            const slot = parseInt(tab.dataset.slot);
            tab.classList.toggle('wardrobe-tab-active', slot === this._activeSlot);
            if (slot >= 0) {
                const name = EquipSlotNames[slot];
                const multiSlots = MULTI_SLOT_MAP[name];
                if (multiSlots) {
                    const hasAny = multiSlots.some(s => this._equipped.get(s));
                    tab.classList.toggle('wardrobe-tab-equipped', hasAny);
                } else {
                    const equipped = this._equipped.get(name) || this._equipped.get(slot);
                    tab.classList.toggle('wardrobe-tab-equipped', !!equipped);
                }
            }
        });
    }

    _filterItems(query) {
        const container = this._root ? this._root.querySelector('#wardrobe-items') : null;
        if (!container) return;
        const lowerQuery = (query || '').toLowerCase().trim();
        const cards = container.querySelectorAll('.wardrobe-item-card');
        cards.forEach(card => {
            if (!lowerQuery) {
                card.style.display = '';
                return;
            }
            const nameEl = card.querySelector('.wardrobe-item-name');
            const name = (nameEl ? nameEl.textContent : '').toLowerCase();
            card.style.display = name.includes(lowerQuery) ? '' : 'none';
        });
    }

    _renderSlotItems() {
        const container = this._root ? this._root.querySelector('#wardrobe-items') : null;
        if (!container) return;
        container.innerHTML = '';

        const locLower = (this._currentLocation || 'Home').toLowerCase();
        const isAtHome = !locLower || locLower.includes('apartment') || locLower.includes('home') || locLower.includes('loft');

        // "All" view: show every owned item grouped by slot
        if (this._activeSlot === -1) {
            const equippedTags = new Set();
            for (const [, item] of this._equipped) {
                if (item) equippedTags.add(item.tag);
            }

            // Show equipped first
            for (const item of this._ownedItems) {
                if (equippedTags.has(item.tag)) {
                    container.appendChild(this._buildItemCard(item, true, false));
                }
            }
            // Then unequipped
            for (const item of this._ownedItems) {
                if (!equippedTags.has(item.tag)) {
                    container.appendChild(this._buildItemCard(item, false, false));
                }
            }

            if (this._ownedItems.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'wardrobe-empty';
                empty.textContent = L.get('wardrobe.no_items');
                container.appendChild(empty);
            }

            this._renderSetBonuses();
            return;
        }

        // Items have slot as string name ("Upper"), activeSlot is numeric index
        const slotName = EquipSlotNames[this._activeSlot];
        const multiSlots = MULTI_SLOT_MAP[slotName];

        const slotItems = this._ownedItems.filter(item => {
            if (multiSlots) return item.slot === slotName;
            return item.slot === slotName || item.slot === this._activeSlot;
        });

        // Collect all equipped items for this tab
        const equippedTags = new Set();
        if (multiSlots) {
            for (const s of multiSlots) {
                const item = this._equipped.get(s);
                if (item) equippedTags.add(item.tag);
            }
        } else {
            const equippedItem = this._equipped.get(slotName) || this._equipped.get(this._activeSlot);
            if (equippedItem) equippedTags.add(equippedItem.tag);
        }

        const isRestricted = RESTRICTED_SLOTS.includes(this._activeSlot) && !isAtHome;

        if (slotItems.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'wardrobe-empty';
            const label = multiSlots ? (L.get(`wardrobe.${slotName.toLowerCase()}s`) || slotName) : slotName;
            empty.textContent = L.get('wardrobe.no_slot_items').replace('{0}', label);
            container.appendChild(empty);
        }

        // Equipped items shown first with highlight
        for (const item of slotItems) {
            if (equippedTags.has(item.tag)) {
                container.appendChild(this._buildItemCard(item, true, isRestricted));
            }
        }

        // Unequipped items
        for (const item of slotItems) {
            if (equippedTags.has(item.tag)) continue;
            const card = this._buildItemCard(item, false, false);
            container.appendChild(card);
        }

        // Update set bonuses
        this._renderSetBonuses();
    }

    _buildItemCard(item, isEquipped, isRestricted) {
        const card = document.createElement('div');
        card.className = 'wardrobe-item-card' + (isEquipped ? ' wardrobe-item-equipped' : '');

        // Icon with hover preview
        const iconEl = document.createElement('div');
        iconEl.className = 'wardrobe-item-icon';
        if (item.icon || item.tag) {
            const imgSrc = `assets/images/Items/${item.icon || item.tag}.png`;
            const img = document.createElement('img');
            img.src = imgSrc;
            img.alt = L.getItem(item.tag, 'name', item.displayName || item.tag);
            img.loading = 'lazy';
            img.onerror = () => { img.style.display = 'none'; };
            iconEl.appendChild(img);

            // Hover preview tooltip
            let preview = null;
            iconEl.addEventListener('mouseenter', () => {
                preview = document.createElement('div');
                preview.className = 'wardrobe-preview-tooltip';
                preview.innerHTML = `<img src="${imgSrc}" alt="${L.getItem(item.tag, 'name', item.displayName || item.tag)}"><div>${L.getItem(item.tag, 'name', item.displayName || item.name || item.tag)}</div>`;
                document.body.appendChild(preview);
                const rect = iconEl.getBoundingClientRect();
                preview.style.left = `${rect.right + 8}px`;
                preview.style.top = `${rect.top}px`;
            });
            iconEl.addEventListener('mouseleave', () => {
                if (preview) { preview.remove(); preview = null; }
            });
        }
        card.appendChild(iconEl);

        // Info
        const info = document.createElement('div');
        info.className = 'wardrobe-item-info';

        const nameRow = document.createElement('div');
        nameRow.className = 'wardrobe-item-name';
        nameRow.textContent = L.getItem(item.tag, 'name', item.displayName || item.name || item.tag);
        if (isEquipped) {
            const badge = document.createElement('span');
            badge.className = 'wardrobe-equipped-badge';
            badge.textContent = L.get('wardrobe.equipped');
            nameRow.appendChild(badge);
        }
        info.appendChild(nameRow);

        // Buffs — items.json uses array format: [{stat, value}]
        const buffs = Array.isArray(item.buffs) ? item.buffs : [];
        if (buffs.length > 0) {
            const buffsEl = document.createElement('div');
            buffsEl.className = 'wardrobe-item-buffs';
            for (const b of buffs) {
                const badge = document.createElement('span');
                badge.className = `wardrobe-buff-badge wardrobe-buff-${b.stat.toLowerCase()}`;
                badge.textContent = `${b.value > 0 ? '+' : ''}${b.value} ${b.stat}`;
                buffsEl.appendChild(badge);
            }
            info.appendChild(buffsEl);
        }

        // Set bonus indicator
        if (item.setId) {
            const setEl = document.createElement('div');
            setEl.className = 'wardrobe-item-set';
            setEl.textContent = `Set: ${item.setId}`;
            info.appendChild(setEl);
        }

        card.appendChild(info);

        // Actions
        const actions = document.createElement('div');
        actions.className = 'wardrobe-item-actions';

        if (isEquipped) {
            const unequipBtn = document.createElement('button');
            unequipBtn.className = 'wardrobe-btn wardrobe-btn-unequip';
            if (isRestricted) {
                _markLockedBtn(unequipBtn, L.get('wardrobe.cant_here'), L.get('wardrobe.undress_at_home'));
            } else if (inventoryManager.isCageKeyholderLocked(item.tag)) {
                // CHASTITY_REMOVAL (0.2.8): someone else holds the key — the cage
                // only comes off through a story beat, not the wardrobe.
                _markLockedBtn(unequipBtn, L.get('wardrobe.cage_locked'), L.get('wardrobe.cage_locked_reason'));
            } else {
                unequipBtn.textContent = L.get('wardrobe.remove');
                unequipBtn.addEventListener('click', () => {
                    events.emit('wardrobe:unequip', item.tag, this._activeSlot);
                    events.emit(Events.AUDIO_SFX_PLAY, 'click2');
                });
            }
            actions.appendChild(unequipBtn);
        } else if (inventoryManager.isOwnedOnlyItem(item)) {
            // Owned-only consumable (dildo, sissy journal, hypno USB, rope set,
            // mummification sleeve): equipping does nothing — owning it (the
            // owns_* flag) is what gates the story action (use the dildo, write
            // in the journal, ...). Show an "Owned" label instead of a no-op
            // Equip button, which would only evict a real buff item from the
            // shared single Item slot. (Discard stays available below.)
            const ownedLabel = document.createElement('span');
            ownedLabel.className = 'wardrobe-owned-label';
            ownedLabel.textContent = L.get('wardrobe.owned') || 'Owned';
            actions.appendChild(ownedLabel);

            // Discard button
            const discardBtn = document.createElement('button');
            discardBtn.className = 'wardrobe-btn wardrobe-btn-discard';
            discardBtn.textContent = L.get('wardrobe.discard') || 'Discard';
            discardBtn.addEventListener('click', () => {
                this._showDiscardConfirm(item);
            });
            actions.appendChild(discardBtn);
        } else {
            const equipBtn = document.createElement('button');
            equipBtn.className = 'wardrobe-btn wardrobe-btn-equip';
            if (inventoryManager.isMascClothingFemLocked(item.tag)) {
                // Feminized past MASC_CLOTHING_FEM_CAP — boy clothes can't go back on.
                _markLockedBtn(equipBtn, L.get('wardrobe.too_fem'), L.get('wardrobe.too_fem_reason'));
            } else {
                equipBtn.textContent = L.get('wardrobe.equip');
                equipBtn.addEventListener('click', () => {
                    // Check for plug first-equip scene (matches Unity WardrobeOverlayController)
                    const firstEquipKnot = this._getPlugFirstEquipKnot(item.tag);
                    if (firstEquipKnot) {
                        // Equip the item, then trigger forced event and close wardrobe
                        events.emit('wardrobe:equip', item.tag, this._activeSlot);
                        events.emit(Events.AUDIO_SFX_PLAY, 'special_pop1');
                        this.close();
                        events.emit('wardrobe:forcedEvent', firstEquipKnot);
                        return;
                    }

                    events.emit('wardrobe:equip', item.tag, this._activeSlot);
                    events.emit(Events.AUDIO_SFX_PLAY, 'switch1');
                });
            }
            actions.appendChild(equipBtn);

            // Discard button
            const discardBtn = document.createElement('button');
            discardBtn.className = 'wardrobe-btn wardrobe-btn-discard';
            discardBtn.textContent = L.get('wardrobe.discard') || 'Discard';
            discardBtn.addEventListener('click', () => {
                this._showDiscardConfirm(item);
            });
            actions.appendChild(discardBtn);
        }

        card.appendChild(actions);
        return card;
    }

    _renderSetBonuses() {
        const container = this._root ? this._root.querySelector('#wardrobe-sets') : null;
        if (!container) return;
        container.innerHTML = '';

        const activeSetNames = new Set(this._activeSets.map(s => s.name));
        const ownedTags = new Set(inventoryManager.getOwnedTags());
        const equippedTags = new Set();
        for (const [_slot, item] of this._equipped) {
            if (item && item.tag) equippedTags.add(item.tag);
        }

        // Active sets
        if (this._activeSets.length > 0) {
            const header = document.createElement('div');
            header.className = 'wardrobe-sets-header';
            header.textContent = L.get('wardrobe.active_sets');
            container.appendChild(header);

            for (const set of this._activeSets) {
                const setEl = document.createElement('div');
                setEl.className = 'wardrobe-set-badge';
                setEl.innerHTML = `
                    <span class="wardrobe-set-name">${set.name}</span>
                    <span class="wardrobe-set-bonus">${this._formatBonuses(set.bonuses)}</span>
                `;
                container.appendChild(setEl);
            }
        }

        // Ready to Equip: all items owned but not all equipped
        const readySets = SET_BONUSES.filter(set => {
            if (activeSetNames.has(set.name)) return false;
            return set.requiredTags.every(tag => ownedTags.has(tag)) &&
                   !set.requiredTags.every(tag => equippedTags.has(tag));
        });

        if (readySets.length > 0) {
            const header = document.createElement('div');
            header.className = 'wardrobe-sets-header';
            header.style.marginTop = '8px';
            header.textContent = L.get('wardrobe.ready_to_equip');
            container.appendChild(header);

            for (const set of readySets) {
                const setEl = document.createElement('div');
                setEl.className = 'wardrobe-set-badge wardrobe-set-ready';
                setEl.style.opacity = '0.85';

                const infoSpan = document.createElement('span');
                infoSpan.innerHTML = `<span class="wardrobe-set-name">${set.name}</span> <span class="wardrobe-set-bonus">${this._formatBonuses(set.bonuses)}</span>`;
                setEl.appendChild(infoSpan);

                // Preview: show stat delta on hover
                const previewEl = document.createElement('div');
                previewEl.className = 'set-preview hidden';
                const bonuses = Array.isArray(set.bonuses) ? set.bonuses : Object.entries(set.bonuses || {}).map(([stat, value]) => ({ stat, value }));
                previewEl.innerHTML = bonuses.map(b =>
                    `<span class="set-preview-stat ${b.value > 0 ? 'buff-pos' : 'buff-neg'}">${b.value > 0 ? '+' : ''}${b.value} ${b.stat}</span>`
                ).join('') + (set.inkFlag ? `<span class="set-preview-flag">${set.inkFlag}</span>` : '');

                const applyBtn = document.createElement('button');
                applyBtn.className = 'wardrobe-btn wardrobe-btn-equip';
                applyBtn.style.cssText = 'margin-left:8px;padding:2px 10px;font-size:11px;';
                applyBtn.textContent = L.get('wardrobe.apply');
                const canHover = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches;
                if (canHover) {
                    applyBtn.addEventListener('mouseenter', () => previewEl.classList.remove('hidden'));
                    applyBtn.addEventListener('mouseleave', () => previewEl.classList.add('hidden'));
                }
                applyBtn.addEventListener('click', () => {
                    for (const tag of set.requiredTags) {
                        if (!equippedTags.has(tag)) {
                            events.emit('wardrobe:equip', tag);
                        }
                    }
                    events.emit(Events.AUDIO_SFX_PLAY, 'switch1');
                    // Toast notification
                    this._showSetToast(set.name);
                    // Flash animation on set badge
                    setEl.classList.add('set-equip-flash');
                    setTimeout(() => setEl.classList.remove('set-equip-flash'), 1200);
                });
                setEl.appendChild(applyBtn);
                setEl.appendChild(previewEl);

                container.appendChild(setEl);
            }
        }

        // Almost Complete: some items owned, some missing
        const almostSets = SET_BONUSES.filter(set => {
            if (activeSetNames.has(set.name)) return false;
            if (readySets.some(r => r.name === set.name)) return false;
            const ownedCount = set.requiredTags.filter(tag => ownedTags.has(tag)).length;
            return ownedCount > 0 && ownedCount < set.requiredTags.length;
        });

        if (almostSets.length > 0) {
            const header = document.createElement('div');
            header.className = 'wardrobe-sets-header';
            header.style.marginTop = '8px';
            header.textContent = L.get('wardrobe.almost_complete');
            container.appendChild(header);

            for (const set of almostSets) {
                const missingTags = set.requiredTags.filter(tag => !ownedTags.has(tag));
                const ownedCount = set.requiredTags.length - missingTags.length;
                const missingNames = missingTags.map(tag => {
                    const item = inventoryManager.getItemByTag(tag);
                    return item ? L.getItem(tag, 'name', item.displayName || item.name || tag) : tag;
                });
                const setEl = document.createElement('div');
                setEl.className = 'wardrobe-set-badge wardrobe-set-almost';
                setEl.style.opacity = '0.6';
                const missingText = L.get('wardrobe.missing').replace('{0}', missingNames.join(', '));
                setEl.innerHTML = `
                    <span class="wardrobe-set-name">${set.name} <span class="wardrobe-set-progress">${ownedCount}/${set.requiredTags.length}</span></span>
                    <span class="wardrobe-set-bonus" style="font-size:10px;color:var(--text-muted)">${missingText}</span>
                `;
                container.appendChild(setEl);
            }
        }

        // Discover Sets: zero items owned. Promoted as collapsible to keep
        // the panel manageable. Filters out Training/Discipline mechanic sets
        // (no `requiredTags` array shape — those are gear-group sets, not
        // cosmetic ones; surfaced via their own UX). Data-deepdive 2026-05-21
        // showed all 7 cosmetic sets at <4% adoption — set system was
        // invisible to anyone who hadn't already bought at least one piece.
        const COSMETIC_SET_FLAGS = new Set([
            'set_full_lingerie', 'set_maid_service', 'set_neko',
            'set_office_ready', 'set_bunny_girl', 'set_cozy_femme', 'set_dark_doll',
        ]);
        const discoverSets = SET_BONUSES.filter(set => {
            if (!COSMETIC_SET_FLAGS.has(set.inkFlag)) return false;
            if (activeSetNames.has(set.name)) return false;
            if (readySets.some(r => r.name === set.name)) return false;
            if (almostSets.some(a => a.name === set.name)) return false;
            const ownedCount = set.requiredTags.filter(tag => typeof tag === 'string' && ownedTags.has(tag)).length;
            return ownedCount === 0;
        });

        if (discoverSets.length > 0) {
            const header = document.createElement('div');
            header.className = 'wardrobe-sets-header wardrobe-sets-discover-header';
            header.style.cssText = 'margin-top:8px;cursor:pointer;display:flex;align-items:center;gap:6px;user-select:none;';
            const caret = document.createElement('span');
            caret.textContent = '▶';
            caret.style.cssText = 'font-size:9px;transition:transform 0.15s ease;display:inline-block;';
            header.appendChild(caret);
            const headerLabel = document.createElement('span');
            headerLabel.textContent = `${L.get('wardrobe.discover_sets')} (${discoverSets.length})`;
            header.appendChild(headerLabel);
            container.appendChild(header);

            const body = document.createElement('div');
            body.className = 'wardrobe-sets-discover-body';
            body.style.cssText = 'display:none;flex-direction:column;gap:4px;margin-top:4px;';

            for (const set of discoverSets) {
                const tagNames = set.requiredTags
                    .filter(t => typeof t === 'string')
                    .map(tag => {
                        const item = inventoryManager.getItemByTag(tag);
                        return item ? L.getItem(tag, 'name', item.displayName || item.name || tag) : tag;
                    });
                const setEl = document.createElement('div');
                setEl.className = 'wardrobe-set-badge wardrobe-set-discover';
                setEl.style.opacity = '0.45';
                setEl.innerHTML = `
                    <span class="wardrobe-set-name">${set.name} <span class="wardrobe-set-bonus" style="font-size:10px;">(${this._formatBonuses(set.bonuses)})</span></span>
                    <span class="wardrobe-set-bonus" style="font-size:10px;color:var(--text-muted)">${tagNames.join(', ')}</span>
                `;
                body.appendChild(setEl);
            }
            container.appendChild(body);

            header.addEventListener('click', () => {
                const open = body.style.display !== 'none';
                body.style.display = open ? 'none' : 'flex';
                caret.style.transform = open ? '' : 'rotate(90deg)';
            });
        }

        // Comfort warning (#11). Equipment-only stat: base is always 0,
        // so getValue would return 0; read getTotalValue (= buff) instead.
        const comfort = variableManager.getTotalValue('Comfort') ?? 0;
        if (comfort < 0) {
            const warning = document.createElement('div');
            warning.className = 'wardrobe-comfort-warning';
            warning.style.cssText = 'margin-top:10px;padding:8px 12px;background:rgba(255,160,0,0.12);border:1px solid rgba(255,160,0,0.3);border-radius:6px;color:#ffa000;font-size:11px;text-align:center;';
            warning.textContent = `\u26A0 ${L.get('wardrobe.low_comfort').replace('{0}', Math.abs(comfort))}`;
            container.appendChild(warning);
        }
    }

    _formatBonuses(bonuses) {
        if (!bonuses) return '';
        // Handle both array format [{stat, value}] and object format {stat: value}
        if (Array.isArray(bonuses)) {
            return bonuses
                .map(b => `${b.value > 0 ? '+' : ''}${b.value} ${b.stat}`)
                .join(', ');
        }
        return Object.entries(bonuses)
            .map(([stat, val]) => `${val > 0 ? '+' : ''}${val} ${stat}`)
            .join(', ');
    }

    _showSetToast(setName) {
        const toast = document.createElement('div');
        toast.className = 'set-equip-toast';
        toast.innerHTML = `<span class="set-toast-icon">✨</span> ${L.get('wardrobe.set_equipped').replace('{0}', setName)}`;
        document.body.appendChild(toast);
        if (typeof gsap !== 'undefined') {
            gsap.fromTo(toast, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3, ease: 'back.out(2)' });
            gsap.to(toast, { y: -10, opacity: 0, duration: 0.3, delay: 2, ease: 'power2.in', onComplete: () => toast.remove() });
        } else {
            setTimeout(() => toast.remove(), 2500);
        }
    }

    // ── Outfit Presets ──────────────────────────────────────────────────

    _loadPresets() {
        try {
            const raw = localStorage.getItem('PnO_WardrobePresets');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    for (let i = 0; i < MAX_PRESETS; i++) {
                        this._presets[i] = parsed[i] || null;
                    }
                }
            }
        } catch (e) {
            // Ignore
        }
    }

    _savePresets() {
        try {
            localStorage.setItem('PnO_WardrobePresets', JSON.stringify(this._presets));
        } catch (e) {
            // Ignore
        }
    }

    _savePreset(index, name) {
        // Snapshot current equipment
        const snapshot = {};
        for (const [slot, item] of this._equipped) {
            if (item) snapshot[slot] = item.tag;
        }
        const presetName = name || this._presets[index]?.name || `Outfit ${index + 1}`;
        this._presets[index] = { name: presetName, items: snapshot, timestamp: Date.now() };
        this._savePresets();

        events.emit(Events.TOAST_REQUEST, `Saved the "${presetName}" look.`, 'toast-success');
        events.emit(Events.AUDIO_SFX_PLAY, 'pop1');

        if (this._isOpen) this._buildUI(), this._renderSlotItems();
    }

    _loadPreset(index) {
        const preset = this._presets[index];
        if (!preset || !preset.items) return;

        events.emit('wardrobe:loadPreset', preset.items);
        events.emit(Events.AUDIO_SFX_PLAY, 'switch1');
        events.emit(Events.TOAST_REQUEST, `Pulled on "${preset.name || 'Outfit'}".`, 'toast-info');
    }

    _deletePreset(index) {
        const name = this._presets[index]?.name || `Outfit ${index + 1}`;
        this._presets[index] = null;
        this._savePresets();
        events.emit(Events.TOAST_REQUEST, `Tossed the "${name}" look.`, 'toast-warning');

        if (this._isOpen) this._buildUI(), this._renderSlotItems();
    }

    _showPresetMenu(index, anchorEl) {
        // Remove existing menus
        const existing = this._root.querySelector('.wardrobe-preset-menu');
        if (existing) existing.remove();

        const menu = document.createElement('div');
        menu.className = 'wardrobe-preset-menu';

        const optionStyle = 'padding:6px 12px; border:none; border-radius:6px; background:transparent; color:var(--text, #e0d0f0); font-size:11px; cursor:pointer; text-align:left; width:100%;';
        const saveOption = document.createElement('button');
        saveOption.className = 'wardrobe-preset-option';
        saveOption.style.cssText = optionStyle;
        saveOption.textContent = L.get('wardrobe.overwrite');
        saveOption.addEventListener('click', () => {
            this._savePreset(index, this._presets[index]?.name);
            menu.remove();
        });
        menu.appendChild(saveOption);

        if (this._presets[index]) {
            const renameOption = document.createElement('button');
            renameOption.className = 'wardrobe-preset-option';
            renameOption.style.cssText = optionStyle;
            renameOption.textContent = L.get('wardrobe.rename');
            renameOption.addEventListener('click', () => {
                menu.remove();
                this._showNameInput({
                    title: L.get('wardrobe.rename_outfit') || 'Rename outfit',
                    initialValue: this._presets[index]?.name || '',
                    placeholder: L.get('wardrobe.outfit_name_ph') || 'Outfit name',
                    confirmLabel: L.get('wardrobe.save') || 'Save',
                    onConfirm: (newName) => {
                        const trimmed = (newName || '').trim();
                        if (!trimmed || !this._presets[index]) return;
                        this._presets[index].name = trimmed;
                        this._savePresets();
                        if (this._isOpen) { this._buildUI(); this._renderSlotItems(); }
                    },
                });
            });
            menu.appendChild(renameOption);

            const deleteOption = document.createElement('button');
            deleteOption.className = 'wardrobe-preset-option wardrobe-preset-delete';
            deleteOption.style.cssText = optionStyle + 'color:#e85c5c;';
            deleteOption.textContent = L.get('wardrobe.delete');
            deleteOption.addEventListener('click', () => {
                this._deletePreset(index);
                menu.remove();
            });
            menu.appendChild(deleteOption);
        }

        // Position near anchor — append to body so it's above all overlays
        const rect = anchorEl.getBoundingClientRect();
        menu.style.cssText = `position:fixed; left:${rect.left}px; bottom:${window.innerHeight - rect.top + 4}px; background:var(--bg-card, #18132a); border:1px solid var(--border, #2e2242); border-radius:8px; padding:4px; display:flex; flex-direction:column; gap:2px; box-shadow:0 4px 16px rgba(0,0,0,0.5); z-index:9998; min-width:120px;`;

        document.body.appendChild(menu);

        // Close on outside click
        const closeHandler = (e) => {
            if (!menu.contains(e.target) && e.target !== anchorEl) {
                menu.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }

    // ── Plug First-Equip ────────────────────────────────────────────────

    /**
     * Check if a plug item needs its first-equip scene.
     * Returns the Ink knot name if the scene hasn't played yet, or null.
     * @param {string} tag - Item tag
     * @returns {string|null}
     */
    _getPlugFirstEquipKnot(tag) {
        const inkVar = PLUG_FIRST_EQUIP_MAP[tag];
        if (!inkVar) return null;

        // Check if the first-equip scene has already played.
        // PLUG_FIRST_EQUIP-flags are Ink-only (not in OBSERVED_VARIABLES) — read
        // from story.variablesState directly. getValue() would return 0/false
        // for non-observed vars and the scene would re-play every wardrobe open.
        try {
            const story = variableManager._story;
            const alreadyDone = story
                ? story.variablesState[inkVar]
                : variableManager.getValue(inkVar);
            if (alreadyDone) return null;
        } catch (e) {
            return null; // Variable doesn't exist or error — skip
        }

        return PLUG_FIRST_EQUIP_KNOTS[inkVar] || null;
    }

    // ── Discard Confirm ────────────────────────────────────────────────

    _showDiscardConfirm(item) {
        // Remove any existing confirm
        document.querySelector('.wardrobe-discard-confirm')?.remove();

        const modal = document.createElement('div');
        modal.className = 'wardrobe-discard-confirm';
        modal.innerHTML =
            `<div class="wardrobe-discard-box">` +
            `<div class="wardrobe-discard-title">${L.get('wardrobe.discard_title') || 'Discard Item'}</div>` +
            `<div class="wardrobe-discard-text">${(L.get('wardrobe.discard_confirm') || 'Remove <b>{name}</b> from your wardrobe? You can buy it again later.').replace('{name}', L.getItem(item.tag, 'name', item.displayName || item.tag))}</div>` +
            `<div class="wardrobe-discard-actions">` +
            `<button class="wardrobe-btn wardrobe-btn-cancel">${L.get('wardrobe.cancel') || 'Cancel'}</button>` +
            `<button class="wardrobe-btn wardrobe-btn-discard-confirm">${L.get('wardrobe.discard') || 'Discard'}</button>` +
            `</div></div>`;

        modal.querySelector('.wardrobe-btn-cancel').addEventListener('click', () => modal.remove());
        modal.querySelector('.wardrobe-btn-discard-confirm').addEventListener('click', () => {
            inventoryManager.removeItemByTag(item.tag);
            events.emit(Events.AUDIO_SFX_PLAY, 'click2');
            events.emit(Events.TOAST_REQUEST, `Tossed the ${L.getItem(item.tag, 'name', item.displayName || item.tag)}.`, 'toast-info');
            events.emit(Events.INVENTORY_CHANGED);
            modal.remove();
            // Refresh wardrobe view immediately
            this.updateInventory(inventoryManager.getOwnedItemObjects());
            this.updateEquipped(inventoryManager.getEquippedMap());
            this._renderSlotItems();
        });

        // Close on backdrop click
        modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
        document.body.appendChild(modal);
    }

    /**
     * In-game text-input modal, replaces window.prompt(), which throws
     * "prompt() is not supported" in Electron (Windows desktop app) and is
     * unreliable inside the Capacitor Android WebView. The thrown error left
     * the wardrobe overlay looking frozen (no name dialog ever appeared).
     * Keyboard-friendly: Enter confirms, Escape / backdrop cancels, input
     * autofocused (and pre-selected for quick rename). onConfirm only fires
     * on an explicit confirm; cancel is a no-op (mirrors prompt() === null).
     */
    _showNameInput({ title, initialValue = '', placeholder = '', confirmLabel, onConfirm }) {
        // Only one such modal at a time
        document.querySelector('.wardrobe-name-modal')?.remove();

        const modal = document.createElement('div');
        modal.className = 'wardrobe-name-modal';

        const box = document.createElement('div');
        box.className = 'wardrobe-name-box';

        const titleEl = document.createElement('div');
        titleEl.className = 'wardrobe-name-title';
        titleEl.textContent = title;
        box.appendChild(titleEl);

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'wardrobe-name-input';
        input.value = initialValue;
        input.placeholder = placeholder;
        input.maxLength = 40;
        box.appendChild(input);

        const actions = document.createElement('div');
        actions.className = 'wardrobe-name-actions';

        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'wardrobe-btn wardrobe-btn-cancel';
        cancelBtn.textContent = L.get('wardrobe.cancel') || 'Cancel';

        const okBtn = document.createElement('button');
        okBtn.className = 'wardrobe-btn wardrobe-btn-name-confirm';
        okBtn.textContent = confirmLabel || L.get('wardrobe.save') || 'Save';

        actions.appendChild(cancelBtn);
        actions.appendChild(okBtn);
        box.appendChild(actions);
        modal.appendChild(box);

        const close = () => modal.remove();
        const confirm = () => {
            const val = input.value;
            close();
            try { onConfirm(val); } catch (e) { console.warn('[Wardrobe] name-input onConfirm failed:', e); }
        };

        cancelBtn.addEventListener('click', close);
        okBtn.addEventListener('click', confirm);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); confirm(); }
            else if (e.key === 'Escape') { e.preventDefault(); close(); }
        });
        // Close on backdrop click (treated as cancel)
        modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

        document.body.appendChild(modal);
        // Autofocus + select existing text for quick rename
        setTimeout(() => { try { input.focus(); input.select(); } catch (e) {} }, 0);
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-wardrobe';
        el.className = 'overlay-container hidden';
        document.body.appendChild(el);
        return el;
    }

    // ── Serialization ───────────────────────────────────────────────────

    serialize() {
        return {
            presets: this._presets,
        };
    }

    deserialize(data) {
        if (data && data.presets) {
            for (let i = 0; i < MAX_PRESETS; i++) {
                this._presets[i] = data.presets[i] || null;
            }
            this._savePresets();
        }
    }
}

/** @type {WardrobeUI} */
export const wardrobeUI = new WardrobeUI();
